import { schoolsGeocoding } from '../types/SchoolGeocoding';
import { corsApiService } from './CorsApiService';
import { debugVerboseLog } from '../utils/debugLog';

export interface GeocodeResult {
    lat: number;
    lng: number;
    source: 'static' | 'nominatim' | 'fallback';
    display_name?: string; // Address from geocoding service
    address?: string; // Full JSON from geocoding service
}

export interface Address {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
}

/**
 * Parse a single-line US address string into components.
 * Handles format: "STREET, CITY, ST ZIP" (e.g. "12261 SUMTER SQUARE DR W, JACKSONVILLE, FL 32218").
 * Returns null if the string cannot be parsed into address, city, state, and zip.
 */
function parseAddressLine(line: string): Address | null {
    const trimmed = (line || '').trim();
    if (!trimmed) return null;
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length < 3) return null;
    const street = parts.slice(0, -2).join(', ').trim();
    const city = parts[parts.length - 2].trim();
    const lastPart = parts[parts.length - 1].trim();
    const stateZipMatch = lastPart.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
    if (!stateZipMatch) {
        const fallback = lastPart.match(/^([A-Za-z]{2})\s+(.+)$/);
        if (fallback) {
            return { address: street, city, state: fallback[1], zip: fallback[2].replace(/\s/g, '') };
        }
        return null;
    }
    const state = stateZipMatch[1];
    const zip = stateZipMatch[2];
    if (!street || !city) return null;
    return { address: street, city, state, zip };
}

export class GeocodingService {
    private static instance: GeocodingService;
    private cache = new Map<string, GeocodeResult>();

    static getInstance(): GeocodingService {
        if (!GeocodingService.instance) {
            GeocodingService.instance = new GeocodingService();
        }
        return GeocodingService.instance;
    }

    /**
     * Get coordinates for an entity, checking static data first, then live geocoding
     */
    async getCoordinates(entity: any, entityType: string): Promise<GeocodeResult | null> {
        // First, check if entity has direct coordinates (coerce to number; API may return strings)
        const lat = Number(entity.Latitude);
        const lng = Number(entity.Longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng, source: 'static' };
        }

        // For schools, check the static geocoding data
        if (entityType.toLowerCase() === 'school' && entity.Id) {
            const staticResult = this.getStaticSchoolCoordinates(entity.Id);
            if (staticResult) {
                return staticResult;
            }
        }

        // Try to geocode from address
        const address = this.extractAddress(entity);
        if (address) {
            return await this.geocodeAddress(address);
        }

        return null;
    }

    /**
     * Get coordinates from static school geocoding data
     */
    private getStaticSchoolCoordinates(schoolId: string): GeocodeResult | null {
        const geocodingEntry = schoolsGeocoding.find(entry => entry.Id === schoolId);

        if (geocodingEntry?.geocode) {
            return {
                lat: geocodingEntry.geocode.lat,
                lng: geocodingEntry.geocode.lng,
                source: 'static'
            };
        }

        return null;
    }

    /**
     * Extract address information from an entity
     */
    private extractAddress(entity: any): Address | null {
        if (!entity) return null;

        const address = entity.Address || entity.address;
        const city = entity.City || entity.city;
        const state = entity.State || entity.state || 'FL';
        const zip = entity.Zip || entity.zip;

        if (address && city && zip) {
            return { address, city, state, zip };
        }

        return null;
    }

    /**
     * Geocode an address using our CORS controller to avoid CORS issues
     */
    private async geocodeAddress(address: Address): Promise<GeocodeResult | null> {
        const cacheKey = `${address.address}, ${address.city}, ${address.state} ${address.zip}`;

        // Check cache first
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey)!;
        }

        try {
            debugVerboseLog(`🗺️ Geocoding address: ${address.address}, ${address.city}, ${address.state} ${address.zip}`);

            // Use our CORS API service
            const result = await corsApiService.geocodeAddress(
                address.address!,
                address.city!,
                address.state!,
                address.zip ?? ''
            );

            if (result && result.lat != null && result.lon != null) {
                const lat = Number(result.lat);
                const lng = Number(result.lon);
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                const geocodeResult: GeocodeResult = {
                    lat,
                    lng,
                    source: 'nominatim',
                    display_name: result.display_name,
                    address: result.address
                };

                // Cache the result
                this.cache.set(cacheKey, geocodeResult);

                debugVerboseLog(`✅ Geocoded successfully: ${geocodeResult.lat}, ${geocodeResult.lng}`);
                return geocodeResult;
            }

            debugVerboseLog(`❌ No geocoding results found for: ${address.address}`);
            throw new Error(`No geocoding results found for address: ${address.address}, ${address.city}, ${address.state} ${address.zip}`);

        } catch (error) {
            console.error('❌ Geocoding error:', error);
            // Re-throw the error so it can be handled by the UI
            if (error instanceof Error) {
                throw error;
            }
            throw new Error(`Geocoding failed: ${error}`);
        }
    }

    /**
     * Get coordinates for a single address string (e.g. from DropOffAddress, PickupAddress).
     * Tries to parse "STREET, CITY, ST ZIP" and call geocode with city/state/zip; otherwise falls back to full-string query.
     */
    async getCoordinatesFromAddressString(fullAddress: string): Promise<GeocodeResult | null> {
        const trimmed = (fullAddress || '').trim();
        if (!trimmed) return null;
        const cacheKey = `query:${trimmed}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey)!;
        }
        try {
            const parsed = parseAddressLine(trimmed);
            if (parsed?.address && parsed?.city && parsed?.zip) {
                try {
                    const result = await this.geocodeAddress(parsed);
                    if (result) {
                        this.cache.set(cacheKey, result);
                        return result;
                    }
                } catch {
                    // Fall through to full-string query
                }
            }
            const result = await corsApiService.geocodeByQuery(trimmed);
            if (result && result.lat != null && result.lon != null) {
                const geocodeResult: GeocodeResult = {
                    lat: result.lat,
                    lng: result.lon,
                    source: 'nominatim',
                    display_name: result.display_name,
                    address: result.address
                };
                this.cache.set(cacheKey, geocodeResult);
                return geocodeResult;
            }
        } catch (e) {
            console.error('Geocoding error for address string:', e);
            throw e;
        }
        return null;
    }

    /**
     * Clear the geocoding cache
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * Get cache statistics
     */
    getCacheStats(): { size: number; entries: string[] } {
        return {
            size: this.cache.size,
            entries: Array.from(this.cache.keys())
        };
    }
}

// Export singleton instance
export const geocodingService = GeocodingService.getInstance();
